import React, { useEffect, useState } from "react";
import "./ReceiptLibrary.css";
import ActionButton from "../components/ActionButton.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import ReadOnlyBanner from "../components/ReadOnlyBanner.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";
import {
  PiFileZip,
  PiSpinnerGap,
  PiSortAscending,
  PiSortDescending,
  PiWarning,
  PiMagnifyingGlass,
  PiX,
} from "react-icons/pi";
import { Lapse } from "../utils/checkout.ts";
import {
  exportReceipts,
  fetchLibraryOrders,
  LibraryOrder as Order,
} from "../utils/exportReceipts.ts";

// Available page size options
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

type SortKey = "name" | "purchase_date" | "status" | "beneficiary" | "amount";
type SortDirection = "asc" | "desc";

interface ReceiptLibraryProps {
  // Set when the account is read-only because a trial or subscription has ended
  lapse?: Lapse | null;
}

const ReceiptLibrary = ({ lapse = null }: ReceiptLibraryProps) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [, setInterestRate] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Add search state
  const [searchQuery, setSearchQuery] = useState(() => {
    const savedQuery = sessionStorage.getItem("receiptSearchQuery");
    return savedQuery || "";
  });
  // Initialize currentPage with value from session storage or default to 1
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("receiptCurrentPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  // Initialize page size from session storage or default to 10
  const [pageSize, setPageSize] = useState(() => {
    const savedPageSize = sessionStorage.getItem("receiptPageSize");
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });
  // Initialize with values from session storage or defaults
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const savedSortKey = sessionStorage.getItem("receiptSortKey");
    return (savedSortKey as SortKey) || "purchase_date";
  });
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    const savedSortDirection = sessionStorage.getItem("receiptSortDirection");
    return (savedSortDirection as SortDirection) || "desc";
  });
  const [showDraftsOnly, setShowDraftsOnly] = useState(false);
  const navigate = useNavigate();

  // Count draft receipts
  const draftReceipts = orders.filter(
    (order) => order.status?.name === "Draft"
  );
  const draftCount = draftReceipts.length;

  // Fetch orders effect
  useEffect(() => {
    // Fetch orders from Supabase
    const fetchOrders = async () => {
      const libraryOrders = await fetchLibraryOrders();
      if (libraryOrders) {
        setOrders(libraryOrders);
      }
    };

    // Fetch interest rate from Supabase
    const fetchInterestRate = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("interest_rate")
          .single(); // Assuming the current user is authenticated

        if (error) {
          console.error("Error fetching interest rate:", error);
        } else {
          setInterestRate(data.interest_rate);
        }
      } catch (error) {
        console.error("Unexpected error fetching interest rate:", error);
      }
    };

    fetchOrders();
    fetchInterestRate();
  }, []);

  // Check for drafts and load filter preference after orders are loaded
  useEffect(() => {
    if (orders.length > 0) {
      const drafts = orders.filter((order) => order.status?.name === "Draft");

      // If there are no drafts, always show all receipts
      if (drafts.length === 0) {
        setShowDraftsOnly(false);
        sessionStorage.setItem("showDraftsOnly", "false");
      } else {
        // Otherwise, load preference from session storage
        const savedDraftsOnly = sessionStorage.getItem("showDraftsOnly");
        if (savedDraftsOnly !== null) {
          setShowDraftsOnly(savedDraftsOnly === "true");
        }
      }
    }
  }, [orders]);

  // Save current page to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("receiptCurrentPage", currentPage.toString());
  }, [currentPage]);

  // Save sort preferences to session storage whenever they change
  useEffect(() => {
    sessionStorage.setItem("receiptSortKey", sortKey);
    sessionStorage.setItem("receiptSortDirection", sortDirection);
  }, [sortKey, sortDirection]);

  // Save page size to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("receiptPageSize", pageSize.toString());
  }, [pageSize]);

  // Save search query to session storage whenever it changes
  const prevSearchQueryRef = React.useRef(searchQuery);
  useEffect(() => {
    sessionStorage.setItem("receiptSearchQuery", searchQuery);
    // Reset to page 1 only when search query actually changes (not on initial render or page changes)
    if (prevSearchQueryRef.current !== searchQuery) {
      prevSearchQueryRef.current = searchQuery;
      setCurrentPage(1);
      sessionStorage.setItem("receiptCurrentPage", "1");
    }
  }, [searchQuery]);

  // TODO: Change this to account for the reimburse schedule
  // const calculateRealizedValue = (amount: number, orderDate: string) => {
  //   if (!interestRate) return 0;

  //   const yearsSinceOrderDate =
  //     (new Date().getTime() - new Date(orderDate).getTime()) /
  //     (1000 * 60 * 60 * 24 * 365);
  //   return (
  //     amount * Math.pow(1 + interestRate / 100, yearsSinceOrderDate) - amount
  //   );
  // };

  const handleExportAllClicked = async () => {
    setExporting(true);
    setExportError(null);
    setExportError(await exportReceipts(orders));
    setExporting(false);
  };

  const handleRowClick = (id: string) => {
    // Find the selected order and pass it through navigation state
    const order = orders.find((order) => order.id === id);
    if (order) {
      navigate(`/receipt-library/${id}`, { state: { order } });
    }
  };

  // Handle showing draft receipts only
  const handleShowDrafts = () => {
    setShowDraftsOnly(true);
    sessionStorage.setItem("showDraftsOnly", "true");
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    sessionStorage.setItem("receiptCurrentPage", "1");
  };

  // Reset filters to show all receipts
  const handleShowAllReceipts = () => {
    setShowDraftsOnly(false);
    sessionStorage.setItem("showDraftsOnly", "false");
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    sessionStorage.setItem("receiptCurrentPage", "1");
  };

  // Handle sort column changes
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      // Toggle direction if already sorting by this key
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      // Set new sort key and default to ascending
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  // Handle page size change
  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    // Reset to page 1 when page size changes
    setCurrentPage(1);
    sessionStorage.setItem("receiptCurrentPage", "1");
  };

  // Add a state to track window width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Handle page dropdown change
  const handlePageDropdownChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const page = parseInt(e.target.value, 10);
    setCurrentPage(page);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Clear the search and return focus to the input
  const handleClearSearch = () => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && searchQuery) {
      handleClearSearch();
    }
  };

  // Sorting and filtering logic
  const filteredAndSortedOrders = React.useMemo(() => {
    // First filter by search query
    let filtered = orders;

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      filtered = orders.filter((order) => {
        // Check if any attachment filename matches the query
        const attachmentMatch =
          order.attachments?.some((attachment) =>
            attachment.original_filename?.toLowerCase().includes(query)
          ) || false;

        // Search across multiple fields
        return (
          order.name?.toLowerCase().includes(query) ||
          false ||
          order.beneficiary?.toLowerCase().includes(query) ||
          false ||
          order.merchant?.toLowerCase().includes(query) ||
          false ||
          order.category?.toLowerCase().includes(query) ||
          false ||
          order.purchase_id?.toLowerCase().includes(query) ||
          false ||
          order.status?.name.toLowerCase().includes(query) ||
          false ||
          attachmentMatch ||
          order.notes?.toLowerCase().includes(query) ||
          false ||
          order.amount?.toString().includes(query) ||
          false ||
          new Date(order.purchase_date)
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              timeZone: "UTC",
            })
            .toLowerCase()
            .includes(query) ||
          false
        );
      });
    }

    // Then filter by draft status if needed
    if (showDraftsOnly) {
      filtered = filtered.filter((order) => order.status?.name === "Draft");
    }

    // Then sort the filtered results
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortKey) {
        case "name":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "purchase_date":
          aValue = new Date(a.purchase_date).getTime();
          bValue = new Date(b.purchase_date).getTime();
          break;
        case "status":
          aValue = a.status["name"] || "";
          bValue = b.status["name"] || "";
          break;
        case "beneficiary":
          aValue = a.beneficiary || "";
          bValue = b.beneficiary || "";
          break;
        case "amount":
          aValue = a.amount || 0;
          bValue = b.amount || 0;
          break;
        default:
          aValue = "";
          bValue = "";
      }

      // Primary sort by the selected column
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

      // Secondary sort by ID to ensure consistent order when primary values are equal
      return a.id < b.id ? -1 : 1;
    });
    return sorted;
  }, [orders, sortKey, sortDirection, showDraftsOnly, searchQuery]);

  // Pagination logic - update to use the dynamic pageSize
  const totalPages = Math.ceil(filteredAndSortedOrders.length / pageSize);
  const pagedOrders = filteredAndSortedOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  // Add additional useEffect to handle order count changes
  useEffect(() => {
    // If orders are loaded and we have pagination info
    if (filteredAndSortedOrders.length > 0 && totalPages > 0) {
      // Make sure current page is valid for the current dataset
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
        sessionStorage.setItem("receiptCurrentPage", totalPages.toString());
      }
    }
  }, [filteredAndSortedOrders.length, totalPages, currentPage]);

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Receipt Library</h1>
            <p>
              {lapse ? "View" : "View and manage"} your existing receipts. Click
              on a receipt below to see additional details.
            </p>
          </div>
          <ActionButton
            icon={exporting ? <PiSpinnerGap className="spin" /> : <PiFileZip />}
            text="Download All"
            onClick={handleExportAllClicked}
            responsive={true}
            secondary={true}
            disabled={exporting}
          />
        </div>

        {lapse && <ReadOnlyBanner lapse={lapse} />}

        {exportError && (
          <InfoBanner icon={<PiWarning size={24} />} color="red">
            <p>{exportError}</p>
          </InfoBanner>
        )}

        {/* Draft receipts banner - only show if drafts exist and we're not already filtering */}
        {!lapse && draftCount > 0 && !showDraftsOnly && (
          <InfoBanner
            icon={<PiWarning size={24} />}
            color="yellow"
            ctaText={"View Receipt" + (draftCount === 1 ? "" : "s")}
            onCtaClick={handleShowDrafts}
          >
            <p>
              You have <strong>{draftCount} email uploaded</strong>
              {draftCount !== 1 ? " receipts" : " receipt"} that{" "}
              {draftCount === 1 ? "needs" : "need"} detail confirmation.
            </p>
          </InfoBanner>
        )}

        {/* Show "View All" banner when filtered to drafts only */}
        {showDraftsOnly && (
          <InfoBanner
            icon={<PiWarning size={24} />}
            color="yellow"
            ctaText="View All Receipts"
            onCtaClick={handleShowAllReceipts}
          >
            <p>
              Currently showing{" "}
              <strong>email uploaded receipts that require confirmation</strong>
              . Click into each receipt to review.
            </p>
          </InfoBanner>
        )}

        <div className="recent-receipts-container receipt-library">
          {/* Search bar */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <PiMagnifyingGlass className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search receipts..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                className="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-button"
                  onClick={handleClearSearch}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <PiX />
                </button>
              )}
            </div>
          </div>

          <div className="receipt-table">
            <table>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="sortable-header">
                      Description
                      {sortKey === "name" &&
                        (sortDirection === "asc" ? (
                          <PiSortDescending className="sort-icon" />
                        ) : (
                          <PiSortAscending className="sort-icon" />
                        ))}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("purchase_date")}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="sortable-header">
                      Date
                      {sortKey === "purchase_date" &&
                        (sortDirection === "asc" ? (
                          <PiSortDescending className="sort-icon" />
                        ) : (
                          <PiSortAscending className="sort-icon" />
                        ))}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="sortable-header">
                      Status
                      {sortKey === "status" &&
                        (sortDirection === "asc" ? (
                          <PiSortDescending className="sort-icon" />
                        ) : (
                          <PiSortAscending className="sort-icon" />
                        ))}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("beneficiary")}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="sortable-header">
                      Beneficiary
                      {sortKey === "beneficiary" &&
                        (sortDirection === "asc" ? (
                          <PiSortDescending className="sort-icon" />
                        ) : (
                          <PiSortAscending className="sort-icon" />
                        ))}
                    </span>
                  </th>
                  <th
                    className="currency"
                    onClick={() => handleSort("amount")}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="sortable-header">
                      Amount
                      {sortKey === "amount" &&
                        (sortDirection === "asc" ? (
                          <PiSortDescending className="sort-icon" />
                        ) : (
                          <PiSortAscending className="sort-icon" />
                        ))}
                    </span>
                  </th>
                  {/* <th className="currency">Realized Value</th> */}
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((order, index) => (
                  <tr key={order.id} onClick={() => handleRowClick(order.id)}>
                    <td title={order.name || ""}>{order.name}</td>
                    <td
                      title={new Date(order.purchase_date).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          timeZone: "UTC",
                        }
                      )}
                    >
                      {new Date(order.purchase_date).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          timeZone: "UTC",
                        }
                      )}
                    </td>
                    <td title={order.status["name"]}>{order.status["name"]}</td>
                    <td title={order.beneficiary || ""}>{order.beneficiary}</td>
                    <td
                      className="currency"
                      title={
                        order.amount
                          ? `$${order.amount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                          : ""
                      }
                    >
                      {order.amount
                        ? `$${order.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                        : ""}
                    </td>
                    {/* <td className="currency green">
                      {order.amount
                        ? `$${calculateRealizedValue(
                            order.amount,
                            order.purchase_date
                          ).toFixed(2)}`
                        : ""}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls - updated with page size selector and responsive pagination */}
          <div className="pagination-container">
            <div className="page-size-selector">
              <label htmlFor="page-size">Show</label>
              <select
                id="page-size"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="page-size-dropdown"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>per page</span>
            </div>

            {totalPages > 1 && (
              <div className="pagination-controls">
                {windowWidth >= 768 ? (
                  // Desktop pagination with buttons
                  <>
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Prev
                    </button>
                    {[...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx + 1}
                        onClick={() => handlePageClick(idx + 1)}
                        className={`pagination-btn${currentPage === idx + 1 ? " active" : ""
                          }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Next
                    </button>
                  </>
                ) : (
                  // Mobile pagination with dropdown
                  <div className="page-size-selector">
                    <label htmlFor="page-dropdown">Page</label>
                    <select
                      id="page-dropdown"
                      value={currentPage}
                      onChange={handlePageDropdownChange}
                      className="page-size-dropdown"
                    >
                      {[...Array(totalPages)].map((_, idx) => (
                        <option key={idx + 1} value={idx + 1}>
                          {idx + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptLibrary;
